import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTelegramApp } from '../../hooks/useTelegramApp';
import { ThermometerIcon, LightningIcon, TrophyIcon, TargetIcon } from '../icons/WeatherIcons';
import './OnboardingFlow.css';

const ONBOARDING_KEY = 'whether-onboarding-complete';

interface Step {
  id: number;
  emoji: string;
  title: string;
  content: React.ReactNode;
}

const steps: Step[] = [
  {
    id: 1,
    emoji: '🌍',
    title: 'Welcome to Whether.market',
    content: (
      <div className="onboarding-step-content">
        <div className="onboarding-globe-container">
          <div className="onboarding-globe">
            <div className="globe-ring globe-ring-1" />
            <div className="globe-ring globe-ring-2" />
            <div className="globe-ring globe-ring-3" />
            <div className="globe-core">🌤️</div>
          </div>
        </div>
        <p className="onboarding-tagline">The world's first weather derivatives exchange</p>
        <p className="onboarding-desc">
          Trade weather outcomes — temperatures, heating degree days, and more —
          with up to 10× leverage using on-chain perpetuals.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    emoji: '⚙️',
    title: 'How Weather Perps Work',
    content: (
      <div className="onboarding-step-content">
        <div className="onboarding-flow-diagram">
          <div className="flow-node">
            <div className="flow-icon"><ThermometerIcon size={24} /></div>
            <div className="flow-label">Weather Station</div>
          </div>
          <div className="flow-arrow">→</div>
          <div className="flow-node">
            <div className="flow-icon">🔮</div>
            <div className="flow-label">Oracle</div>
          </div>
          <div className="flow-arrow">→</div>
          <div className="flow-node">
            <div className="flow-icon">📈</div>
            <div className="flow-label">Index</div>
          </div>
          <div className="flow-arrow">→</div>
          <div className="flow-node highlight">
            <div className="flow-icon">🧑‍💻</div>
            <div className="flow-label">You Trade</div>
          </div>
        </div>
        <p className="onboarding-desc">
          <strong>Go long</strong> if you think temperatures will rise.{' '}
          <strong>Go short</strong> if you think they'll fall.
        </p>
        <p className="onboarding-desc secondary">
          Funding rates keep perps tethered to the real weather index — the oracle settles everything.
        </p>
      </div>
    ),
  },
  {
    id: 3,
    emoji: '🏛️',
    title: 'Settlement',
    content: (
      <div className="onboarding-step-content">
        <div className="onboarding-settlement-badge">
          <div className="settlement-check">✓</div>
          <p>Real government weather data settles every trade.</p>
          <p className="settlement-sub">No counterparty risk.</p>
        </div>
        <div className="onboarding-oracle-sources">
          <div className="oracle-source">
            <div className="oracle-logo noaa">NOAA</div>
            <span>National Oceanic & Atmospheric Administration</span>
          </div>
          <div className="oracle-source">
            <div className="oracle-logo metoffice">Met Office</div>
            <span>UK Meteorological Office</span>
          </div>
          <div className="oracle-source">
            <div className="oracle-logo ecmwf">ECMWF</div>
            <span>European Centre for Medium-Range Weather Forecasts</span>
          </div>
        </div>
        <p className="onboarding-desc secondary">
          All data is verified on-chain before settlement. No human can manipulate it.
        </p>
      </div>
    ),
  },
  {
    id: 4,
    emoji: '🎯', // target emoji for step 4
    title: 'Your First Trade',
    content: (
      <div className="onboarding-step-content">
        <div className="onboarding-trade-steps">
          <div className="trade-step">
            <div className="trade-step-num">1</div>
            <div className="trade-step-text">
              <strong>Pick a market</strong>
              <span>NYC HDD, Dallas CDD, or 30+ weather products</span>
            </div>
          </div>
          <div className="trade-step">
            <div className="trade-step-num">2</div>
            <div className="trade-step-text">
              <strong>Set your size</strong>
              <span>Enter position size in TON</span>
            </div>
          </div>
          <div className="trade-step">
            <div className="trade-step-num">3</div>
            <div className="trade-step-text">
              <strong>Choose leverage</strong>
              <span>1× to 10× — higher leverage means higher risk</span>
            </div>
          </div>
          <div className="trade-step">
            <div className="trade-step-num">4</div>
            <div className="trade-step-text trade-step-action">
              <strong>Click Long or Short</strong>
              <div className="trade-preview-btns">
                <span className="preview-long">▲ Long</span>
                <span className="preview-short">▼ Short</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 5,
    emoji: '⭐',
    title: 'Earn Points',
    content: (
      <div className="onboarding-step-content">
        <div className="onboarding-xp-visual">
          <div className="xp-icon"><LightningIcon size={24} /></div>
          <div className="xp-text">
            <div className="xp-headline">Every trade earns XP</div>
            <div className="xp-sub">The more you trade, the more you earn</div>
          </div>
        </div>
        <div className="onboarding-rewards">
          <div className="reward-item">
            <span className="reward-icon"><TrophyIcon size={20} /></span>
            <div className="reward-text">
              <strong>Climb the leaderboard</strong>
              <span>Compete with traders worldwide</span>
            </div>
          </div>
          <div className="reward-item">
            <span className="reward-icon">🪂</span>
            <div className="reward-text">
              <strong>Whether Airdrop</strong>
              <span>Top traders earn the Whether token airdrop</span>
            </div>
          </div>
          <div className="reward-item">
            <span className="reward-icon"><TargetIcon size={20} /></span>
            <div className="reward-text">
              <strong>Streak Bonuses</strong>
              <span>Daily trading streaks multiply your XP</span>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 6,
    emoji: '💰',
    title: 'Get Your Testnet Wallet',
    content: (
      <div className="onboarding-step-content">
        <p className="onboarding-desc">Whether.market is on TON testnet. Get set up in 4 steps:</p>
        <div className="onboarding-wallet-steps">
          <div className="wallet-step"><span className="wallet-step-num">1</span><div><strong>Install a TON Wallet</strong><p>Get <a href="https://tonkeeper.com" target="_blank" rel="noopener">Tonkeeper</a> or <a href="https://mytonwallet.io" target="_blank" rel="noopener">MyTonWallet</a></p></div></div>
          <div className="wallet-step"><span className="wallet-step-num">2</span><div><strong>Switch to Testnet</strong><p>Tonkeeper: Settings → Network → Testnet</p></div></div>
          <div className="wallet-step"><span className="wallet-step-num">3</span><div><strong>Get Free Test TON</strong><p>Send your address to <a href="https://t.me/testgiver_ton_bot" target="_blank" rel="noopener">@testgiver_ton_bot</a> on Telegram</p></div></div>
          <div className="wallet-step"><span className="wallet-step-num">4</span><div><strong>Connect and Trade</strong><p>Click Connect Wallet on Whether.market</p></div></div>
        </div>
      </div>
    ),
  },
  {
    id: 7,
    emoji: '🚀',
    title: "Let's Go!",
    content: (
      <div className="onboarding-step-content onboarding-final">
        <div className="final-rocket">🚀</div>
        <p className="final-headline">You're ready to trade weather</p>
        <p className="onboarding-desc">
          Start with a small position to get familiar with how the markets move.
          The weather is always changing — and so is your opportunity.
        </p>
        <div className="final-stats">
          <div className="final-stat">
            <span className="final-stat-val">30+</span>
            <span className="final-stat-label">Markets</span>
          </div>
          <div className="final-stat">
            <span className="final-stat-val">10×</span>
            <span className="final-stat-label">Max Leverage</span>
          </div>
          <div className="final-stat">
            <span className="final-stat-val">24/7</span>
            <span className="final-stat-label">Trading</span>
          </div>
        </div>
      </div>
    ),
  },
];

interface OnboardingFlowProps {
  onComplete?: () => void;
  forceShow?: boolean;
}

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete, forceShow = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const { webApp } = useTelegramApp();

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (!completed || forceShow) {
      // Small delay so the app renders first
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  const handleComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setVisible(false);
    webApp?.HapticFeedback?.notificationOccurred('success');
    onComplete?.();
    navigate('/perps');
  };

  const handleSkip = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    setVisible(false);
    onComplete?.();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      webApp?.HapticFeedback?.selectionChanged();
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      webApp?.HapticFeedback?.selectionChanged();
    }
  };

  if (!visible) return null;

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        {/* Skip button */}
        <button className="onboarding-skip" onClick={handleSkip}>
          Skip
        </button>

        {/* Step indicator emoji */}
        <div className="onboarding-step-emoji">{step.emoji}</div>

        {/* Title */}
        <h2 className="onboarding-title">{step.title}</h2>

        {/* Content */}
        <div className="onboarding-body">{step.content}</div>

        {/* Navigation */}
        <div className="onboarding-nav">
          <button
            className="onboarding-btn-secondary"
            onClick={handlePrev}
            disabled={currentStep === 0}
          >
            ← Back
          </button>

          <button
            className={`onboarding-btn-primary ${isLast ? 'onboarding-btn-cta' : ''}`}
            onClick={handleNext}
          >
            {isLast ? "🚀 Let's Trade!" : 'Next →'}
          </button>
        </div>

        {/* Progress dots */}
        <div className="onboarding-dots">
          {steps.map((_, i) => (
            <button
              key={i}
              className={`onboarding-dot ${i === currentStep ? 'active' : ''} ${i < currentStep ? 'done' : ''}`}
              onClick={() => setCurrentStep(i)}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export { ONBOARDING_KEY };
export default OnboardingFlow;
